import { MenuItem, Select, SelectChangeEvent } from '@mui/material';

import { styled } from '@liscio/ui';

export type BalanceType = 'amount_due' | 'refund';

const BALANCE_TYPE_OPTIONS: { value: BalanceType; label: string }[] = [
  { value: 'refund', label: 'REFUND' },
  { value: 'amount_due', label: 'AMOUNT DUE' },
];

export interface BalanceTypeTagProps {
  value: BalanceType;
  onChange?: (type: BalanceType) => void;
  disabled?: boolean;
  editable?: boolean;
}

const StyledSelect = styled(Select<BalanceType>, {
  shouldForwardProp: (prop) => prop !== 'balanceType',
})<{ balanceType: BalanceType }>(({ theme, balanceType }) => {
  const isRefund = balanceType === 'refund';
  return {
    width: theme.spacing(16),
    marginLeft: theme.spacing(2),
    height: theme.spacing(3),
    backgroundColor: isRefund
      ? theme.palette.common.success['100']
      : theme.palette.common.error['100'],
    color: isRefund
      ? theme.palette.common.success['700']
      : theme.palette.common.error['600'],
    borderRadius: theme.spacing(2),
    '& .MuiSelect-select': {
      padding: theme.spacing(0, 1.5),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      height: '100%',
      fontWeight: 600,
      fontSize: theme.typography.body2.fontSize,
      lineHeight: 1,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '& .MuiSelect-icon': {
      color: isRefund
        ? theme.palette.common.success['700']
        : theme.palette.common.error['600'],
    },
  };
});

/**
 * A unified tag component that displays the balance type as a colored tag.
 * When `editable` is true, the tag acts as a dropdown to switch between
 * "Amount Due" and "Refund". When not editable, it displays as a static tag.
 */
export const BalanceTypeTag = ({
  value,
  onChange,
  disabled = false,
  editable = false,
}: BalanceTypeTagProps) => {
  const handleChange = (event: SelectChangeEvent<BalanceType>) => {
    onChange?.(event.target.value as BalanceType);
  };

  return (
    <StyledSelect
      value={value}
      onChange={editable ? handleChange : undefined}
      size="small"
      disabled={disabled}
      balanceType={value}
      readOnly={!editable}
      IconComponent={editable ? undefined : () => null}
      sx={
        !editable
          ? {
              pointerEvents: 'none',
              '& .MuiSelect-select': {
                paddingRight: '8px !important',
              },
            }
          : undefined
      }
    >
      {BALANCE_TYPE_OPTIONS.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </StyledSelect>
  );
};

// Re-export for backwards compatibility
export const BalanceTypeDropdown = BalanceTypeTag;
